import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

import { api, ApiError } from "../lib/api";
import { ConfirmDialog, AlertDialog } from "../components/ConfirmDialog";
import { BiField } from "../components/BiField";
import { SearchSelect } from "../components/SearchSelect";

type SiteUser = { id: number; email: string; display_name: string; company: string | null };
type LinkableSub = { id: number; title: string; status: string; created_at: string };

/**
 * 智库报告明细 / 编辑页。点击报告列表行进入。
 * 可改标题/链接/赛道/摘要等，可改可见性、打开报告文件、删除。
 */
type Report = {
  id: number;
  source: "user" | "kexi";
  user_id: number | null;
  submission_id: number | null;
  title_zh: string;
  title_en: string | null;
  report_url: string | null;
  slug: string | null;
  file_url: string | null;
  site_url: string | null;
  sector: string | null;
  pages: number | null;
  report_count: number | null;
  summary_zh: string | null;
  summary_en: string | null;
  visibility: "private" | "pending" | "public";
  created_at: string;
  user_email: string | null;
  user_name: string | null;
  user_company: string | null;
};

const VIS_LABEL: Record<Report["visibility"], string> = { private: "私密", pending: "待审核", public: "已公开" };

export function StudioReportDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [r, setR] = useState<Report | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [okMsg, setOkMsg] = useState<string | null>(null);
  const [confirmDel, setConfirmDel] = useState(false);

  // 可编辑字段（含来源 source 与交付用户 user_id）
  const [f, setF] = useState({ source: "user" as "user" | "kexi", user_id: null as number | null, submission_id: null as number | null, title_zh: "", title_en: "", report_url: "", slug: "", sector: "", pages: "", report_count: "", summary_zh: "", summary_en: "" });
  const [users, setUsers] = useState<SiteUser[]>([]);
  const [subs, setSubs] = useState<LinkableSub[]>([]);

  useEffect(() => {
    api.get<Report>(`/api/admin/studio/reports/${id}`)
      .then((data) => {
        setR(data);
        setF({
          source: data.source, user_id: data.user_id != null ? Number(data.user_id) : null,
          submission_id: data.submission_id != null ? Number(data.submission_id) : null,
          title_zh: data.title_zh ?? "", title_en: data.title_en ?? "",
          report_url: data.report_url ?? "", slug: data.slug ?? "", sector: data.sector ?? "",
          pages: data.pages != null ? String(data.pages) : "",
          report_count: data.report_count != null ? String(data.report_count) : "",
          summary_zh: data.summary_zh ?? "", summary_en: data.summary_en ?? "",
        });
      })
      .catch((e) => setErr(e instanceof ApiError && e.status === 404 ? "报告不存在" : String(e)));
    api.get<SiteUser[]>("/api/admin/studio/users").then(setUsers).catch(() => {});
  }, [id]);

  // 选中交付用户后，加载其名下可关联的提交（未被别的报告占用；报告↔提交一一对应）
  useEffect(() => {
    if (f.user_id == null) { setSubs([]); return; }
    api.get<LinkableSub[]>(`/api/admin/studio/reports/${id}/linkable-submissions?user_id=${f.user_id}`)
      .then(setSubs).catch(() => setSubs([]));
  }, [f.user_id, id]);

  if (err) return <div><div className="page-header"><div className="page-title">报告明细</div></div><div className="error">{err}</div></div>;
  if (!r) return <div className="muted" style={{ fontSize: 13 }}>加载中…</div>;

  const upd = (patch: Partial<typeof f>) => setF((prev) => ({ ...prev, ...patch }));
  const fileUrl = r.report_url || r.site_url || r.file_url;

  const save = async () => {
    // 交付用户可留空（未分配报告；之后可在智库提交页关联时再分配）
    setSaving(true); setErr(null);
    try {
      const updated = await api.patch<Report>(`/api/admin/studio/reports/${r.id}`, {
        source: f.source,
        user_id: f.source === "kexi" ? null : f.user_id,
        submission_id: f.source === "kexi" ? null : f.submission_id,
        title_zh: f.title_zh.trim(),
        title_en: f.title_en.trim() || null,
        report_url: f.report_url.trim() || null,
        slug: f.slug.trim() || null,
        sector: f.sector.trim() || null,
        pages: f.pages ? Number(f.pages) : null,
        report_count: f.report_count ? Number(f.report_count) : null,
        summary_zh: f.summary_zh.trim() || null,
        summary_en: f.summary_en.trim() || null,
      });
      setR(updated);
      setOkMsg("已保存。");
    } catch (e) { setErr(e instanceof ApiError ? `保存失败：${JSON.stringify(e.body)}` : String(e)); }
    finally { setSaving(false); }
  };

  const setVisibility = async (visibility: Report["visibility"]) => {
    try { const u = await api.patch<Report>(`/api/admin/studio/reports/${r.id}`, { visibility }); setR(u); }
    catch (e) {
      const code = e instanceof ApiError ? (e.body as { error?: string } | null)?.error : null;
      setErr(code === "report_url_required_for_public"
        ? "自动获取公开链接失败（智库不可达或 slug 无效）：请稍后重试，或在上方「报告网页链接」手动填写并保存后再批准公开。"
        : code === "intel_project_not_public"
          ? "该项目在智库侧尚未公开：请先在智库仓库 projects.js 给该项目加 public: true 并部署（同步 robots.txt），再批准公开，否则公开链接会被登录墙拦截。"
          : String(e));
    }
  };

  const doDelete = async () => {
    setConfirmDel(false);
    try { await api.delete(`/api/admin/studio/reports/${r.id}`); nav("/studio/reports"); }
    catch (e) { setErr(String(e)); }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">报告明细</div>
          <div className="muted" style={{ marginTop: 4, fontSize: 12 }}>
            {r.source === "kexi" ? "珂玺研究" : <>交付给 {r.user_name}{r.user_company ? ` · ${r.user_company}` : ""}（{r.user_email}）</>}
            　·　当前可见性：{VIS_LABEL[r.visibility]}　·　创建于 {new Date(r.created_at).toLocaleString("zh-CN", { hour12: false })}
          </div>
        </div>
        <div className="page-actions" style={{ display: "flex", gap: 8 }}>
          <button className="btn" onClick={() => nav("/studio/reports")}>← 返回列表</button>
          {fileUrl && <a className="btn" href={fileUrl} target="_blank" rel="noopener noreferrer">打开报告文件</a>}
        </div>
      </div>

      {err && <div className="error" style={{ marginBottom: 12 }}>{err}</div>}

      <div style={{ display: "grid", gap: 16, maxWidth: 720 }}>
        <div className="form">
          <div className="ce-label" style={{ marginBottom: 12 }}>报告信息</div>

          <div className="ce-field">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div>
                <div className="ce-label">来源</div>
                <select value={f.source} onChange={(e) => upd({ source: e.target.value as "user" | "kexi" })} style={{ width: "100%" }}>
                  <option value="user">用户提交制作</option>
                  <option value="kexi">珂玺自有研究</option>
                </select>
              </div>
              {f.source === "user" && (
                <div>
                  <div className="ce-label">交付给用户</div>
                  <SearchSelect
                    value={f.user_id}
                    onChange={(v) => upd({ user_id: v === null ? null : Number(v), submission_id: null })}
                    placeholder="输入姓名 / 邮箱 / 公司搜索…"
                    options={users.map((u) => ({ value: Number(u.id), label: u.display_name, hint: `${u.email}${u.company ? ` · ${u.company}` : ""}` }))}
                  />
                </div>
              )}
            </div>
          </div>

          {f.source === "user" && f.user_id != null && (
            <div className="ce-field">
              <div className="ce-label">关联提交（该用户的申请记录，一份报告对应一条）</div>
              <SearchSelect
                value={f.submission_id}
                onChange={(v) => upd({ submission_id: v === null ? null : Number(v) })}
                placeholder="选择要挂载的提交申请…"
                options={subs.map((s) => ({ value: s.id, label: s.title || `提交 #${s.id}`, hint: new Date(s.created_at).toLocaleDateString("zh-CN") }))}
              />
            </div>
          )}

          <BiField label="报告标题" cn={f.title_zh} en={f.title_en} onCn={(v) => upd({ title_zh: v })} onEn={(v) => upd({ title_en: v })} placeholder="报告标题" />

          <div className="ce-field">
            <div className="ce-label">报告网页链接（公开直链；pdf/pptx 含在该网页内）</div>
            <input className="ce-input" placeholder="https://..." value={f.report_url} onChange={(e) => upd({ report_url: e.target.value })} />
          </div>

          <div className="ce-field">
            <div className="ce-label">智库项目 slug（定向报告用，由智库方提供；与链接至少填一个）</div>
            <input className="ce-input" placeholder="如 riscv-2026" value={f.slug} onChange={(e) => upd({ slug: e.target.value })} />
          </div>

          <div className="ce-field">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
              <div>
                <div className="ce-label">赛道</div>
                <input className="ce-input" placeholder="赛道（选填）" value={f.sector} onChange={(e) => upd({ sector: e.target.value })} />
              </div>
              <div>
                <div className="ce-label">页数</div>
                <input className="ce-input" type="number" placeholder="页数" value={f.pages} onChange={(e) => upd({ pages: e.target.value })} />
              </div>
              <div>
                <div className="ce-label">报告份数</div>
                <input className="ce-input" type="number" placeholder="份数" value={f.report_count} onChange={(e) => upd({ report_count: e.target.value })} />
              </div>
            </div>
            <div className="muted" style={{ fontSize: 11, marginTop: 6 }}>页数与报告份数任填一个，前台按「页数优先」显示（如 42 页 / 3 份报告）。</div>
          </div>

          <BiField label="摘要（选填）" rows={2}
            cn={f.summary_zh} en={f.summary_en} onCn={(v) => upd({ summary_zh: v })} onEn={(v) => upd({ summary_en: v })} placeholder="摘要" />

          <button className="btn btn-primary" disabled={saving || !f.title_zh.trim()} onClick={save}>{saving ? "保存中…" : "保存"}</button>
        </div>

        <div className="form">
          <div className="ce-label" style={{ marginBottom: 12 }}>可见性与操作</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {r.visibility !== "public" && <button className="btn" onClick={() => setVisibility("public")}>{r.visibility === "pending" ? "批准公开" : "直接公开"}</button>}
            {r.visibility !== "private" && <button className="btn" onClick={() => setVisibility("private")}>设为私密</button>}
            <button className="btn" style={{ color: "#b04a3a", borderColor: "#b04a3a" }} onClick={() => setConfirmDel(true)}>删除报告</button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={confirmDel}
        title="删除报告"
        message={<>确认删除报告「{r.title_zh}」？报告及其文件将被永久删除，不可恢复。</>}
        confirmLabel="删除"
        danger
        onConfirm={doDelete}
        onCancel={() => setConfirmDel(false)}
      />
      <AlertDialog open={okMsg !== null} title="操作成功" message={okMsg} onClose={() => setOkMsg(null)} />
    </div>
  );
}
